import { WeekToDateProps } from "reports/pages/GenericReports/WeekToDate/WeekToDateTypes";
import { ONE_MINUTE_OF_MS } from "reports/utils/timeUtils";
import { PnlCategoriesEnum, SpecialSubTagType } from "reports/types";
import { pciMachines } from "reports/pages/customers/pci/Utils/pciUtils";
import { MetricEnum, TimeUnitsEnum } from "reports/utils/metricTypes";
import { PnlBreakdown } from "reports/getters/pnlBreakdownGetter/pnlBreakdownTypes";
import { SummaryMetricInput } from "reports/utils/metricUtils";

// So we don't have to individually list the machines (there are over 30), we use An Object entries mapping to generate them
const pciMachinesValueStreamMapping = Object.entries(pciMachines).map(
  ([machineName]) => ({
    name: machineName,
    link: `focusroom/${machineName}`,
  })
);
export const CUSTOMERS_WITH_REPORTS = {
  pci: [
    ...pciMachinesValueStreamMapping,
    { name: "All - EOS", link: "eos/all" },
    { name: "Blister - EOS", link: "eos/blister" },
    { name: "Biotech - EOS", link: "eos/biotech" },
    { name: "Bottle - EOS", link: "eos/bottle" },
  ],
  pciuk: [
    { name: "All - EOS", link: "eos/all" },
    { name: "Unit 1 - EOS", link: "eos/unit1" },
    { name: "Unit 2 - EOS", link: "eos/unit2" },
  ],
  qi: [{ name: "Qiagen Quarterly", link: "Quarterly" }],
  qiqc: [{ name: "Weekly Tagging Overview", link: "WeeklyTag" }],
  steris: [{ name: "Weekly", link: "weekly" }],
  naturesway: [
    { name: "EOS", link: "eos" },
    { name: "Main Line", link: "week-to-date?machineId=main_line" },
    { name: "Merril", link: "week-to-date?machineId=merril" },
    { name: "Cremer", link: "week-to-date?machineId=cremer" },
    { name: "Liquids", link: "week-to-date?machineId=liquids" },
  ],
  nwmason: [
    { name: "EOS", link: "eos" },
    { name: "Packaging - WTD", link: "week-to-date?machineId=packaging" },
  ],
  ravenjest: [{ name: "EOS", link: "eos" }],
};

type ReportTargetsType = {
  oeeTarget: number;
  unitsPerShiftTarget: number;
  unitsPerDayTarget: number;
  lineSpeedTarget: {
    target: number; // rate value
    time: number; // units per x time (hours, minutes, etc)
  };
  netOutputDivisor: number;
  // when this customer was deployed,
  // or certain configs for valid data to be showing on reports
  validDataStartDate: string;
};

export const REPORT_TARGETS: { [tenant: string]: ReportTargetsType } = {};

export type CustomerConfigType = {
  [customerName: string]: { [machineId: string]: WeekToDateProps };
};

type CustomerLocationDetails = {
  custName: string;
  custLocation: string;
  timeZone: string;
};
const natureswayLocationDetails: CustomerLocationDetails = {
  custName: "Naturesway",
  custLocation: "Green Bay, Wisconsin",
  timeZone: "America/Menominee",
};

/**
 * Used to generate the props for most of the week to date configurations
 */
const generateWeekToDateProps = ({
  unitsProducedPerMin,
  dayStartDetails: { dayStartHour, dayStartMinute },
  shiftsPerDay,
  daysActiveInWeek,
}) => ({
  unitsProducedPerMin,
  unitsProducedPerMs: unitsProducedPerMin / (1000 * 60),
  dayStartHour,
  dayStartMinute,
  activeDays: daysActiveInWeek,
  activeShifts: shiftsPerDay,
  shiftsPerWeek: daysActiveInWeek * shiftsPerDay,
});

const morning5AMStart = { dayStartHour: 5, dayStartMinute: 0 };
const morning6AMStart = { dayStartHour: 6, dayStartMinute: 0 };

const naturesWayMachineProps = (
  machemetryId,
  processName,
  unitsProducedPerMin: number,
  staticOeeTarget: number
): WeekToDateProps => ({
  machemetryId,
  processName,
  ...generateWeekToDateProps({
    unitsProducedPerMin,
    dayStartDetails: morning5AMStart,
    shiftsPerDay: 2,
    daysActiveInWeek: 7,
  }),
  staticOeeTarget,
  ...natureswayLocationDetails,
  manualData: [],
  targets: [],
  activeShiftsRatio: 1,
});

const naturesWaytwoMachineProps = (
  machemetryId,
  processName,
  unitsProducedPerMin: number,
  staticOeeTarget: number
): WeekToDateProps => ({
  machemetryId,
  processName,
  ...generateWeekToDateProps({
    unitsProducedPerMin,
    dayStartDetails: morning6AMStart,
    shiftsPerDay: 3,
    daysActiveInWeek: 7,
  }),
  staticOeeTarget,
  ...natureswayLocationDetails,
  manualData: [],
  targets: [],
  activeShiftsRatio: 1,
});

const naturesWayMason2MachineProps = (
  machemetryId,
  processName,
  unitsProducedPerMin: number,
  staticOeeTarget: number
): WeekToDateProps => ({
  machemetryId,
  processName,
  ...generateWeekToDateProps({
    unitsProducedPerMin,
    dayStartDetails: morning5AMStart,
    shiftsPerDay: 1,
    daysActiveInWeek: 5,
  }),
  staticOeeTarget,
  ...natureswayLocationDetails,
  manualData: [],
  targets: [],
  activeShiftsRatio: 1,
});

export const WEEK_TO_DATE_CUSTOMER_CONFIG: CustomerConfigType = {
  naturesway: {
    main_line: {
      ...naturesWayMachineProps("naturesway_d_001", "Main Line", 160, 31),
    },
    merril: {
      ...naturesWayMachineProps("naturesway_d_005", "Merril", 168, 33),
    },
    cremer: {
      ...naturesWayMachineProps("naturesway_d_006", "Cremer", 100, 31),
    },
    liquids: {
      ...naturesWayMachineProps("naturesway_d_007", "Liquids", 100, 31),
    },
  },
  natureswaytwo: {
    encapsulation_1: {
      ...naturesWaytwoMachineProps(
        "naturesway_d_002",
        "Encapsulation 1",
        1920.0,
        25
      ),
      shouldHideTarget: true,
    },
  },
  nwmason: {
    packaging: {
      ...naturesWayMachineProps("nwmason_d_001", "Packaging", 100, 0),
      shouldHideTarget: true,
    },
  },
  nwmason2: {
    manufacturing: {
      ...naturesWayMason2MachineProps("nwmason2_d_002", "Manufacturing", 25, 0), // units per minute, and target unimportant due to hidden targets
      shouldHideTarget: true,
    },
  },
};

export const EOS_REPORT_TIME_DIVISOR = {
  pai: ONE_MINUTE_OF_MS,
};

export const FLIGHT_LINK_TO_MACHEMETRY_ID_MAPPING = {
  pciuk: {
    pciuk_d_001: "02/0",
    pciuk_d_002: "01/0",
    pciuk_d_003: "01/1",
    pciuk_d_004: "01/2",
    pciuk_d_005: "01/4",
    pciuk_d_006: "01/3",
    pciuk_d_007: "01/6",
    pciuk_d_008: "01/5",
    pciuk_d_009: "02/1",
    pciuk_d_010: "02/2",
  },
};

export const GENERIC_PNL_CATEGORY_PROPS_ARR = (
  producingSpecialSubTags?: SpecialSubTagType[]
) => [
  {
    category: PnlCategoriesEnum.producing,
    ...(producingSpecialSubTags &&
      producingSpecialSubTags.length > 0 && {
        specialSubTags: producingSpecialSubTags,
      }),
  },
  { category: PnlCategoriesEnum.planned },
  { category: PnlCategoriesEnum.unplanned },
  { category: PnlCategoriesEnum.waiting },
  { category: PnlCategoriesEnum.unknown },
  {
    category: PnlCategoriesEnum.notScheduled,
    shouldHideValue: true,
  },
  {
    category: PnlCategoriesEnum.uncollected,
    shouldHideValue: true,
  },
];
/** Customers for whom we want to show new config FIP features */
export const CUSTOMERS_WITH_CONFIG_FIP = [
  "raveng",
  "ravenjest",
  "ravenmanufacturing",
];

/** returns `true` if we're on internal URLs shimmer.raven.ai or localhost:3000 */
export const getIsInternalUrl = () => {
  return ["localhost:4000", "shimmer.raven.ai"].includes(window.location.host);
};

export const SKU_METRIC_COLUMNS_PER_CUSTOMER: {
  [tenant: string]: {
    metrics?: SummaryMetricInput[];
    getCustomSummaryMetricsInputListWithPnlBreakdown?: (
      pnlBreakdown: PnlBreakdown
    ) => SummaryMetricInput[];
    customSortFn?: (a: PnlBreakdown, b: PnlBreakdown) => number;
  };
} = {
  portlandforge: {
    metrics: [
      { name: MetricEnum.SKU },
      { name: MetricEnum.PROCESS_NAME },
      { name: MetricEnum.COUNT },
      { name: MetricEnum.TARGET_RATE },
      { name: MetricEnum.PLANNED_TIME, unit: TimeUnitsEnum.hoursAndMinutes },
      { name: MetricEnum.UNPLANNED_TIME, unit: TimeUnitsEnum.hoursAndMinutes },
      { name: MetricEnum.WAITING_TIME, unit: TimeUnitsEnum.hoursAndMinutes },
      {
        name: MetricEnum.PRODUCING_TIME,
        title: "Producing",
        unit: TimeUnitsEnum.hoursAndMinutes,
        order: 13,
      },
    ],
    getCustomSummaryMetricsInputListWithPnlBreakdown:
      getPortlandForgeSummaryMetricsInputListWithPnlBreakdown,
    customSortFn: (a, b) => {
      // sort by process name
      if (a.processNames[0] < b.processNames[0]) {
        return -1;
      }
      if (a.processNames[0] > b.processNames[0]) {
        return 1;
      }
      // sort by sku
      if (!a.sku || !b.sku) {
        return 0;
      }
      if (a.sku < b.sku) {
        return -1;
      }
      if (a.sku > b.sku) {
        return 1;
      }
      return 0;
    },
  },
  pciuk: {
    metrics: [
      { name: MetricEnum.SKU },
      { name: MetricEnum.SKU_CODE },
      { name: MetricEnum.PROCESS_NAME, title: "Cubicle" },
      { name: MetricEnum.COUNT },
      { name: MetricEnum.TARGET_COUNT },
      {
        name: MetricEnum.OPERATION_TIME,
        unit: TimeUnitsEnum.hoursAndMinutes,
      },
      { name: MetricEnum.OEE, title: "Line Efficiency" },
    ],
  },
  "eccomfg-calgary": {
    metrics: [
      { name: MetricEnum.SKU },
      { name: MetricEnum.SKU_CODE },
      { name: MetricEnum.PROCESS_NAME },
      { name: MetricEnum.COUNT },
      { name: MetricEnum.TARGET_RATE },
      {
        name: MetricEnum.OPERATION_TIME,
        unit: TimeUnitsEnum.hoursAndMinutes,
      },
      { name: MetricEnum.OEE },
      { name: MetricEnum.PERF },
      { name: MetricEnum.AVAIL },
    ],
  },
  "portlandforge-lebanon": {
    metrics: [
      { name: MetricEnum.SKU, order: 1 },
      { name: MetricEnum.SKU_CODE, title: "Description", order: 2 },
      { name: MetricEnum.PROCESS_NAME, title: "Process Name", order: 3 },
      { name: MetricEnum.COUNT, order: 4 },
      { name: MetricEnum.TARGET_RATE, title: "Target Rate", order: 5 },
      {
        name: MetricEnum.PLANNED_TIME,
        unit: TimeUnitsEnum.hoursAndMinutes,
        title: "Planned",
        order: 6,
      },
      {
        name: MetricEnum.UNPLANNED_TIME,
        unit: TimeUnitsEnum.hoursAndMinutes,
        title: "Unplanned",
        order: 8,
      },
      {
        name: MetricEnum.WAITING_TIME,
        unit: TimeUnitsEnum.hoursAndMinutes,
        title: "Waiting",
        order: 10,
      },
      {
        name: MetricEnum.PRODUCING_TIME,
        unit: TimeUnitsEnum.hoursAndMinutes,
        title: "Producing",
        order: 12,
      },
    ],
    getCustomSummaryMetricsInputListWithPnlBreakdown:
      getPortlandForgeLebanonSummaryMetricsInputListWithPnlBreakdown,
    customSortFn: (a, b) => {
      // sort by process name
      if (a.processNames[0] < b.processNames[0]) {
        return -1;
      }
      if (a.processNames[0] > b.processNames[0]) {
        return 1;
      }
      // sort by sku
      if (!a.sku || !b.sku) {
        return 0;
      }
      if (a.sku < b.sku) {
        return -1;
      }
      if (a.sku > b.sku) {
        return 1;
      }
      return 0;
    },
  },
  quepasa: {
    metrics: [
      { name: MetricEnum.SKU },
      { name: MetricEnum.SKU_CODE },
      { name: MetricEnum.PROCESS_NAME },
      { name: MetricEnum.COUNT, title: "Gross Count" },
      { name: MetricEnum.UPTIME },
      { name: MetricEnum.AVG_RUN_RATE, title: "Actual Rate" },
      { name: MetricEnum.TARGET_RATE },
      { name: MetricEnum.PERF },
      { name: MetricEnum.REJECT_COUNT },
    ],
  },
};

function getPortlandForgeSummaryMetricsInputListWithPnlBreakdown(
  pnlBreakdown: PnlBreakdown
) {
  return [
    {
      title: "Heating",
      customValue:
        pnlBreakdown?.waiting?.tags.find(({ tagName }) => tagName === "Heating")
          ?.durationMs ?? 0,
      order: 10,
      unit: TimeUnitsEnum.hoursAndMinutes,
    },
    {
      title: "Holder Change",
      customValue:
        pnlBreakdown?.planned?.tags.find(
          ({ tagName }) => tagName === "Holder Change"
        )?.durationMs ?? 0,
      order: 11,
      unit: TimeUnitsEnum.hoursAndMinutes,
    },
    {
      title: "Block Change",
      customValue:
        pnlBreakdown?.planned?.tags.find(
          ({ tagName }) => tagName === "Block Change"
        )?.durationMs ?? 0,
      order: 11,
      unit: TimeUnitsEnum.hoursAndMinutes,
    },
    {
      title: "Die Change",
      customValue:
        pnlBreakdown?.planned?.tags.find(
          ({ tagName }) => tagName === "Die Change"
        )?.durationMs ?? 0,
      order: 11,
      unit: TimeUnitsEnum.hoursAndMinutes,
    },
  ];
}
function getPortlandForgeLebanonSummaryMetricsInputListWithPnlBreakdown(
  pnlBreakdown: PnlBreakdown
) {
  return [
    {
      title: "Die Change",
      customValue:
        pnlBreakdown?.planned?.tags.find(
          ({ tagName }) => tagName === "Die Change"
        )?.durationMs ?? 0,
      order: 7,
      unit: TimeUnitsEnum.hoursAndMinutes,
    },
    {
      title: "Die Problem",
      customValue:
        pnlBreakdown?.unplanned?.tags.find(
          ({ tagName }) => tagName === "Die Change"
        )?.durationMs ?? 0,
      order: 9,
      unit: TimeUnitsEnum.hoursAndMinutes,
    },
    {
      title: "Heating",
      customValue:
        pnlBreakdown?.waiting?.tags.find(({ tagName }) => tagName === "Heating")
          ?.durationMs ?? 0,
      order: 11,
      unit: TimeUnitsEnum.hoursAndMinutes,
    },
  ];
}
